// Pure, framework-free logic for outbound transactional email configuration
// (see `features/passwordless-login.md`). TAE sends email through ListMonk; the
// connection details of each ListMonk instance and the per-language template
// ids live in the database — not in code or env — because different regions
// will eventually route through different ListMonk instances, and each UI
// language has its own template. This module owns the shared, testable rules
// around that configuration: the message-purpose catalog, per-language template
// selection, the preferred-language resolution the login flow uses, and input
// validation. It exposes the *view* projections the API returns to the client
// (which never include an instance's secret token).
//
// Kept import-only on the pure `languages` module so it stays shareable by the
// client (to render the messaging admin) and the server (to enforce it).

import {
  DEFAULT_LANGUAGE,
  isLanguageCode,
  type LanguageCode,
} from './languages';

/**
 * The purposes a transactional template can serve. The codes are stable — they
 * are stored as plain text and referenced by the flows that send each kind of
 * email — so never rename one; add new entries instead.
 *
 * - `PASSWORDLESS_LOGIN` — the magic-link sign-in email.
 * - `ATHLETE_INVITE` — "claim your profile", sent to an athlete themselves
 *   (whether invited by the club or by their guardian).
 * - `GUARDIAN_INVITE` — "your child's profile", sent to a parent or carer who
 *   will hold access on a minor's behalf.
 *
 * The two invite purposes are separate rather than one template branching on a
 * `relation` field because the copy differs throughout — who the profile belongs
 * to, what the recipient is being asked to do, and what they can do afterwards
 * (see `features/athlete-access.md`).
 *
 * The three company-signup purposes carry a sport club's self-registration
 * (see `features/company-registration.md`):
 *
 * - `COMPANY_SIGNUP_CODE` — the email-verification passcode the wizard's first
 *   step sends to the prospective Company Admin.
 * - `COMPANY_SIGNUP_RECEIVED` — "we are checking your company details", sent
 *   when the wizard is completed and the (unverified) company is created.
 * - `COMPANY_VERIFIED` — sent to the company's admins when a Super Admin marks
 *   the company as verified.
 */
export const MESSAGE_PURPOSES = [
  'PASSWORDLESS_LOGIN',
  'ATHLETE_INVITE',
  'GUARDIAN_INVITE',
  'COMPANY_SIGNUP_CODE',
  'COMPANY_SIGNUP_RECEIVED',
  'COMPANY_VERIFIED',
] as const;

/** A transactional message purpose. */
export type MessagePurpose = (typeof MESSAGE_PURPOSES)[number];

/** The passwordless-login purpose. */
export const PASSWORDLESS_LOGIN: MessagePurpose = 'PASSWORDLESS_LOGIN';

/** The "claim your athlete profile" invitation purpose. */
export const ATHLETE_INVITE: MessagePurpose = 'ATHLETE_INVITE';

/** The "access your child's athlete profile" invitation purpose. */
export const GUARDIAN_INVITE: MessagePurpose = 'GUARDIAN_INVITE';

/** The company-signup email-verification passcode purpose. */
export const COMPANY_SIGNUP_CODE: MessagePurpose = 'COMPANY_SIGNUP_CODE';

/** The "we are checking your company details" confirmation purpose. */
export const COMPANY_SIGNUP_RECEIVED: MessagePurpose = 'COMPANY_SIGNUP_RECEIVED';

/** The "your company has been verified" notification purpose. */
export const COMPANY_VERIFIED: MessagePurpose = 'COMPANY_VERIFIED';

/**
 * The template purpose an invitation of `relation` is sent under: a guardian
 * gets the parent/carer copy, the athlete themselves the "claim your profile"
 * copy. Kept here so the server and the messaging admin agree on the mapping.
 */
export function invitePurposeFor(relation: 'SELF' | 'GUARDIAN'): MessagePurpose {
  return relation === 'GUARDIAN' ? GUARDIAN_INVITE : ATHLETE_INVITE;
}

/** Whether `value` is a known message purpose. */
export function isMessagePurpose(value: unknown): value is MessagePurpose {
  return (
    typeof value === 'string' &&
    (MESSAGE_PURPOSES as readonly string[]).includes(value)
  );
}

/**
 * Region key of the platform-default ListMonk instance — the one used until a
 * region-specific instance is added (region-based routing is a documented
 * extension point; see the feature doc). A region is any stable label an
 * operator chooses (`default`, `asia`, `europe`…).
 */
export const DEFAULT_REGION = 'default';

/**
 * ListMonk transactional `subscriber_mode` values. `external` sends to any
 * address without requiring a subscriber row (the right default for login
 * emails, whose recipients are platform users, not marketing subscribers);
 * `default` requires the subscriber to exist; `fallback` looks one up but sends
 * regardless. See ListMonk's transactional API docs.
 */
export const SUBSCRIBER_MODES = ['external', 'default', 'fallback'] as const;

/** A ListMonk transactional subscriber-lookup mode. */
export type SubscriberMode = (typeof SUBSCRIBER_MODES)[number];

/** The subscriber mode used when none is chosen (login recipients aren't subscribers). */
export const DEFAULT_SUBSCRIBER_MODE: SubscriberMode = 'external';

/** Whether `value` is a known ListMonk subscriber mode. */
export function isSubscriberMode(value: unknown): value is SubscriberMode {
  return (
    typeof value === 'string' &&
    (SUBSCRIBER_MODES as readonly string[]).includes(value)
  );
}

/**
 * A ListMonk instance as exposed to the messaging admin UI — everything about
 * the connection *except* its secret `token`, which never leaves the server.
 * Keyed by an auto-assigned integer, like `Company`.
 */
export interface ListMonkInstanceView {
  id: number;
  region: string;
  baseUrl: string;
  username: string;
  /** Whether a token is stored (so the UI can show "configured" without it). */
  hasToken: boolean;
  /** ListMonk transactional subscriber lookup mode (see `SUBSCRIBER_MODES`). */
  subscriberMode: SubscriberMode;
  /** The platform-default instance, used when no region-specific one matches. */
  isDefault: boolean;
  createdAt: number;
}

/**
 * A per-(instance, purpose, language) template mapping as exposed to the admin
 * UI. `templateId` is the numeric ListMonk transactional-template id — only
 * meaningful inside its owning instance, which is why a template always belongs
 * to one.
 */
export interface MessageTemplateView {
  id: number;
  instanceId: number;
  purpose: MessagePurpose;
  language: LanguageCode;
  templateId: number;
  createdAt: number;
}

/**
 * Pick the template id for `language` from a purpose's templates, falling back
 * to the platform-default language (English) and then to any available
 * template, so a login email is never blocked purely for want of an exact
 * language match. Returns null only when the purpose has no template at all.
 */
export function selectTemplateId(
  templates: readonly { language: LanguageCode; templateId: number }[],
  language: LanguageCode,
  fallback: LanguageCode = DEFAULT_LANGUAGE,
): number | null {
  const exact = templates.find((t) => t.language === language);
  if (exact) return exact.templateId;
  const fallbackMatch = templates.find((t) => t.language === fallback);
  if (fallbackMatch) return fallbackMatch.templateId;
  return templates.length > 0 ? templates[0].templateId : null;
}

/**
 * Path (under the app origin) of the raster platform logo the transactional
 * email templates embed. Email clients cannot render SVG, so this points at
 * the PNG copy in `public/`, which the single-container deployment serves at
 * the app origin alongside the SPA.
 */
export const EMAIL_LOGO_PATH = '/logo-email.png';

/**
 * Absolute URL of the platform logo for a transactional email, built against
 * the same trusted origin as the emailed link (see `server/appOrigin.ts`).
 * Rendered by the templates as `{{ .Tx.Data.logo_url }}`.
 */
export function buildEmailLogoUrl(origin: string): string {
  const base = origin.endsWith('/') ? origin.slice(0, -1) : origin;
  return `${base}${EMAIL_LOGO_PATH}`;
}

/**
 * The language a message to a user should use: their own stored preference
 * first, then their company's default, then the platform default (English).
 * Non-code values are ignored so a stale row can never select a bad language.
 */
export function resolvePreferredLanguage(
  userLanguage: unknown,
  companyDefault: unknown,
): LanguageCode {
  if (isLanguageCode(userLanguage)) return userLanguage;
  if (isLanguageCode(companyDefault)) return companyDefault;
  return DEFAULT_LANGUAGE;
}

/** Max length accepted for a region label / username / base URL / token. */
export const MESSAGING_FIELD_MAX = 512;

/** Trim + lowercase a region label to its canonical stored form. */
export function normalizeRegion(region: string): string {
  return region.trim().toLowerCase();
}

/** Whether `value` looks like an absolute http(s) URL (a ListMonk base URL). */
export function isHttpUrl(value: string): boolean {
  return /^https?:\/\/[^\s]+$/.test(value.trim());
}

/** A ListMonk instance's writable fields (what the admin form submits). */
export interface ListMonkInstanceInput {
  region: string;
  baseUrl: string;
  username: string;
  /** Omitted on an update that keeps the existing token. */
  token?: string;
  /** ListMonk subscriber mode; omitted falls back to `DEFAULT_SUBSCRIBER_MODE`. */
  subscriberMode?: string;
  isDefault?: boolean;
}

/**
 * Validate a ListMonk instance input, returning a map of field -> message for
 * whatever is wrong (empty when valid). `requireToken` is false on an update
 * that may keep the stored token untouched.
 */
export function validateInstanceInput(
  input: ListMonkInstanceInput,
  requireToken: boolean,
): Record<string, string> {
  const errors: Record<string, string> = {};
  const region = normalizeRegion(input.region ?? '');
  if (region.length === 0) {
    errors.region = 'A region label is required.';
  } else if (region.length > MESSAGING_FIELD_MAX) {
    errors.region = 'The region label is too long.';
  }
  const baseUrl = (input.baseUrl ?? '').trim();
  if (baseUrl.length === 0) {
    errors.baseUrl = 'A ListMonk base URL is required.';
  } else if (!isHttpUrl(baseUrl) || baseUrl.length > MESSAGING_FIELD_MAX) {
    errors.baseUrl = 'Enter a valid http(s) URL.';
  }
  if ((input.username ?? '').trim().length === 0) {
    errors.username = 'An API username is required.';
  }
  if (requireToken || input.token !== undefined) {
    if ((input.token ?? '').length === 0) {
      errors.token = 'An API token is required.';
    }
  }
  if (input.subscriberMode !== undefined && !isSubscriberMode(input.subscriberMode)) {
    errors.subscriberMode = 'Choose a valid subscriber mode.';
  }
  return errors;
}

/** A template mapping's writable fields (what the admin form submits). */
export interface MessageTemplateInput {
  instanceId: number;
  purpose: string;
  language: string;
  templateId: number;
}

/**
 * Validate a template mapping input, returning a map of field -> message for
 * whatever is wrong (empty when valid).
 */
export function validateTemplateInput(
  input: MessageTemplateInput,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!Number.isInteger(input.instanceId) || input.instanceId < 1) {
    errors.instanceId = 'Choose a ListMonk instance.';
  }
  if (!isMessagePurpose(input.purpose)) {
    errors.purpose = 'Choose a valid message purpose.';
  }
  if (!isLanguageCode(input.language)) {
    errors.language = 'Choose a supported language.';
  }
  if (!Number.isInteger(input.templateId) || input.templateId < 1) {
    errors.templateId = 'Enter the numeric ListMonk template id.';
  }
  return errors;
}
